import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from "react";

import { pdf } from "@react-pdf/renderer";
import { Button, QRCode } from "antd";
import { toast } from "react-toastify";

import PdfPreviewModal from "../../../Common/UIComponents/PdfPreviewModal";
import SampleFunctionContext from "../../SampleFunctionContext";
import SampleSubmissionContext from "../../SampleSubmissionContext";
import ReceiptPDF from "./ReceiptPDF/ReceiptPDF";

import { getCurrentYear } from "../../../../utils/common";

import { getCustomerAddress, getSubmittedSubmissionsInfo } from "../../../../actions/sampleSubmission";

import { ReactComponent as InfoIcon } from "../../../../assets/icons/Sample Submission Redesign/print-info.svg";

import "./SubmissionReceiptModal.css";

const FOOTER_TEXT = [
  `© ${getCurrentYear()} Genista, All rights reserved`,
  "Genista Biosciences, Inc. 5500 Hellyer Avenue, San Jose, CA. 95138 P. 1.408.934.6575",
];

const CUSTOMER_INFO_FIELDS_BY_ROW = [
  [{ label: "Attn", key: "user_name" },
    { label: "Company", key: "company" }],
  [{ label: "Phone", key: "telephone" },
    { label: "Fax", key: "fax" }],
  [{ label: "Email", key: "user_email" },
    { label: "Address", key: "address_concat" }],
];

export default function SubmissionReceiptModal({
  showReceipt,
  handleReceipt,
  submissionFromParent = null,
  submissionId,
  customerId,
  closable = true,
  maskClosable = true,
  po, // needed because po is not included in getSubmission api response
}) {
  const { sampleCategory, isLoggedOut, thirdParty } = useContext(SampleSubmissionContext);
  const { setOnAddBackButton } = useContext(SampleFunctionContext);
  const [loadingCustomerInfo, setLoadingCustomerInfo] = useState(true);
  const [customerInfo, setCustomerInfo] = useState({});
  const [submission, setSubmission] = useState(submissionFromParent);
  const [loadingSubmission, setLoadingSubmission] = useState(!submissionFromParent);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const getCustomerInfo = async () => {
    const result = await getCustomerAddress((isLoggedOut && !thirdParty) ? customerId : undefined, sampleCategory);
    if (result.success) {
      const addressArr = [];
      if (result.address.address_1) {
        addressArr.push(result.address.address_1);
      }
      if (result.address.address_2) {
        addressArr.push(result.address.address_2);
      }
      result.address.address_concat = addressArr.join(", ");
      setCustomerInfo(result.address);
    } else {
      toast.error(result.message);
    }
    setLoadingCustomerInfo(false);
  };

  const getSubmission = async () => {
    setLoadingSubmission(true);
    const payload = {
      submission_id: submissionId,
      page: 1,
      sample_search: "",
      submission_search: "",
      search_by: "",
      date_from: "",
      date_to: "",
      user_selected_date_from: "",
      user_selected_date_to: "",
      submitted_by: "",
      sample_category: sampleCategory,
    };

    const {
      success,
      message,
      result,
    } = await getSubmittedSubmissionsInfo(payload);

    if (success) {
      setSubmission({ ...result, submit_id: submissionId, po });
    } else {
      toast.error(message);
    }
    setLoadingSubmission(false);
  };

  useEffect(() => {
    getCustomerInfo();
    if (!submissionFromParent) {
      getSubmission();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getFileName = useCallback(() => {
    const companyName = customerInfo.company;
    const submissionName = submission.submit_name;
    const timestamp = Date.now();
    const fileName = `${companyName}_${submissionName}_${timestamp}.pdf`;
    return fileName.replace(/\s|\//g, "_");
  }, [customerInfo.company, submission]);

  const dataReady = !loadingCustomerInfo && !loadingSubmission && Boolean(submission);

  useEffect(() => {
    if (!showReceipt || !dataReady) {
      setPdfBlob(null);
      setGeneratingPdf(false);
      return undefined;
    }

    let cancelled = false;
    setGeneratingPdf(true);

    const generatePdf = async () => {
      try {
        const blob = await pdf(ReceiptPDF({
          submission,
          customerInfo,
          CUSTOMER_INFO_FIELDS_BY_ROW,
          FOOTER_TEXT,
          getFileName,
        })).toBlob();

        if (cancelled) return;

        if (!blob || blob.size === 0) {
          toast.error("Failed to generate pdf");
          setPdfBlob(null);
          return;
        }

        setPdfBlob(blob);
      } catch (error) {
        if (cancelled) return;
        toast.error("Failed to generate pdf");
        setPdfBlob(null);
      } finally {
        if (!cancelled) {
          setGeneratingPdf(false);
        }
      }
    };

    generatePdf();

    return () => {
      cancelled = true;
    };
  }, [customerInfo, dataReady, getFileName, showReceipt, submission]);

  const previewFiles = useMemo(() => {
    if (!pdfBlob || !submission) return [];

    return [{
      key: `receipt-${submission.submit_id || submission.submit_name}`,
      fileName: getFileName(),
      src: pdfBlob,
    }];
  }, [getFileName, pdfBlob, submission]);

  const handleOnCancel = () => {
    handleReceipt();
    setOnAddBackButton(false);
  };

  const qrCodeValue = submission?.submit_id || submission?.submit_name;

  return (
    <>
      {showReceipt && submission && (
        <div className="SubmissionReceiptModal__HiddenQr" aria-hidden>
          <div id="sample_submission_receipt_qrcode">
            <QRCode
              status="active"
              value={qrCodeValue}
              type="canvas"
              size={180}
              bordered={false}
              color="#111a24"
              bgColor="#ffffff"
            />
          </div>
        </div>
      )}
      <PdfPreviewModal
        open={showReceipt}
        title={(
          <span className="SubmissionReceiptModal__PrintHint">
            <InfoIcon className="SubmissionReceiptModal__PrintHintIcon" width={14} height={14} aria-hidden />
            <span className="SubmissionReceiptModal__PrintHintText">
              Print out this receipt and include it with the sample contents in your package.
            </span>
          </span>
        )}
        onClose={handleOnCancel}
        files={previewFiles}
        loading={!dataReady || generatingPdf}
        showDownload
        showPrint
        width="40vw"
        maskClosable={maskClosable}
        errorMessage="Unable to preview this receipt."
        className="SubmissionReceiptModal__PdfPreview"
        footer={!closable ? (
          <Button
            onClick={handleOnCancel}
            type="primary"
          >
            Done
          </Button>
        ) : null}
      />
    </>
  );
}
